/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import {
  Hidden,
  SelectField,
  SelectOption,
  Tab,
  TabContext,
  TabPanel,
  Tabs,
  Typography,
  breakpoints,
  typography,
  typography2,
  useTheme,
} from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import type {
  EntWebsiteBlockContentQuery,
  EntWebsiteContentFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout from 'bundles/ent-website/contentful/core/BlockLayout';
import Button from 'bundles/ent-website/contentful/core/ContentfulButton';
import Image from 'bundles/ent-website/contentful/core/Image';
import Markdown from 'bundles/ent-website/contentful/core/Markdown';
import { entWebsiteBlockContentQuery } from 'bundles/ent-website/queries/EntWebsiteContent';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';

import _t from 'i18n!nls/ent-website';

type Props = BlockModuleInnerProps<EntWebsiteContentFragment>;

const styles = {
  description: css`
    ${typography.h2}
    margin-top: var(--cds-spacing-300);
    margin-bottom: var(--cds-spacing-1000);

    ${breakpoints.down('xs')} {
      margin-bottom: var(--cds-spacing-600);
    }
  `,
  contentWrapper: (_theme: Theme, isDarkBG: boolean) => css`
    .cds-tab-list-indicator {
      background-color: ${isDarkBG ? 'var(--cds-color-white-0)' : 'var(--cds-color-interactive-primary)'} !important;
    }
  `,
  tabs: (_theme: Theme, isDarkBG: boolean) => css`
    margin-bottom: var(--cds-spacing-600);

    .cds-tab-wrapper {
      ${typography2.subtitleMedium}
      color: ${isDarkBG ? 'var(--cds-color-white-0)' : 'inherit'} !important;
    }

    button:hover {
      background: none;
    }

    button:focus {
      outline: none;
    }

    button.cds-tab-selected {
      border: 0;
      border-radius: 0;
      outline: none;
    }
  `,
  tabContent: css`
    display: flex;
    padding-bottom: var(--cds-spacing-200);

    ${breakpoints.down('xs')} {
      flex-direction: column;
    }

    a {
      padding-left: 0;
    }

    .description-and-cta {
      width: 40%;
      margin-top: var(--cds-spacing-600);
      padding-right: var(--cds-spacing-600);

      ${breakpoints.down('xs')} {
        width: 100%;
        padding-right: 0;
      }
    }

    .how-description {
      margin-bottom: var(--cds-spacing-300);

      * {
        color: var(--cds-color-neutral-primary-invert);
        ${typography2.subtitleLarge}
        /* Override CDS styles weight */
        font-weight: 400;
      }

      a {
        text-underline-offset: 2px;
        font-weight: bold;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    .how-image {
      flex-grow: 1;
      padding: 0 var(--cds-spacing-600);
      max-height: 400px;

      ${breakpoints.down('xs')} {
        margin-top: var(--cds-spacing-800);
        padding: 0;
      }
    }

    .how-image img {
      object-fit: contain;
      height: 100%;
    }

    .how-ctaButton {
      padding-left: 0;
    }
  `,
};

const THEME_2_IS_DARK_BG: Record<string, boolean> = {
  'white-bg': false,
  'grey-bg': false,
  'light-blue-bg': false,
  'blue-bg-invert-fg': true,
  'black-bg': true,
  'dark-blue-bg-invert-fg': true,
};

export function ContentModule({ content, assembly }: Props) {
  const theme = useTheme();
  const isDarkBG = THEME_2_IS_DARK_BG[assembly?.theme ?? ''] ?? false;
  const { tabNames } = content;

  const [currentTab, setCurrentTab] = useState<string>('0');

  const handleMenuSelection = (event: React.ChangeEvent<{ value: unknown }>) => {
    setCurrentTab(event.target.value as string);
  };

  const contentPanel = () => (
    <div css={styles.contentWrapper(theme, isDarkBG)}>
      <Typography variant="d2semibold" color="inherit">
        {content.headline}
      </Typography>

      <Typography variant="h2" color="inherit" css={styles.description}>
        {content.description}
      </Typography>

      <Hidden smUp>
        <SelectField
          defaultValue="0"
          aria-label={_t('Select tab')}
          onChange={handleMenuSelection}
          fullWidth
          value={currentTab}
        >
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-expect-error */}
          {tabNames?.map((tab, index) => (
            <SelectOption value={`${index}`} key={tab}>
              {tab}
            </SelectOption>
          ))}
        </SelectField>
      </Hidden>

      <TabContext value={`${currentTab}`}>
        <Hidden xsDown>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-expect-error */}
          <Tabs
            aria-label={_t('Content tabs')}
            css={styles.tabs(theme, isDarkBG)}
            variant="section"
            keyboardActivation="automatic"
          >
            {content.tabNames?.map((tab, index) => (
              <Tab
                label={tab ?? ''}
                value={`${index}`}
                onClick={() => {
                  setCurrentTab(`${index}`);
                }}
              />
            ))}
          </Tabs>
        </Hidden>

        {content?.howsCollection?.items.map((tabContent, index) => (
          <TabPanel value={`${index}`} key={tabContent?.slug}>
            <div css={styles.tabContent}>
              <div className="description-and-cta">
                <div>
                  <Markdown className="how-description" source={tabContent?.description} module={content} />
                  {tabContent?.ctaText && (
                    <Button
                      variant={isDarkBG ? 'ghostInvert' : 'ghost'}
                      component="a"
                      href={tabContent.ctaTarget}
                      module={content}
                      icon={<ArrowNextIcon />}
                      position={index}
                    >
                      {tabContent.ctaText}
                    </Button>
                  )}
                </div>
              </div>

              <div className="how-image">{tabContent?.image && <Image content={tabContent.image} width="100%" />}</div>
            </div>
          </TabPanel>
        ))}
      </TabContext>
    </div>
  );

  return <BlockLayout assembly={assembly} renderContent={contentPanel} />;
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockContentModule = withBlockQuery<EntWebsiteContentFragment, EntWebsiteBlockContentQuery>(
  entWebsiteBlockContentQuery
)(ContentModule);
